import React from 'react';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="mb-8">
          <a href="/" className="text-sm text-blue-600 hover:text-blue-800">&larr; Back to Banyak Group</a>
        </div>
        <div className="bg-white rounded-xl shadow-sm border p-8 space-y-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Terms of Service</h1>
            <p className="text-sm text-gray-500 mt-1">Last updated: July 21, 2026</p>
          </div>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">1. Acceptance of Terms</h2>
            <p className="text-sm text-gray-700 leading-relaxed">
              By accessing or using the Banyak Group platform at app.banyak.group ("the Platform"), you agree to be bound by these Terms of Service. 
              If you do not agree to these terms, you may not use the Platform.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">2. Description of Service</h2>
            <p className="text-sm text-gray-700 leading-relaxed">
              Banyak Group provides a recruitment and pipeline management platform that enables recruitment firms to manage candidate pipelines, 
              collaborate with client firms, schedule interviews, and track the hiring process. The Platform includes features for candidate management, 
              client portals, interview scheduling, calendar integrations, outreach campaigns (email and SMS), in-app voice calling, and reporting.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">3. User Accounts</h2>
            <p className="text-sm text-gray-700 leading-relaxed">
              You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. 
              You agree to provide accurate and complete information when creating your account and to update your information as necessary. 
              You must notify us immediately of any unauthorized use of your account.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">4. Acceptable Use</h2>
            <p className="text-sm text-gray-700 leading-relaxed">
              You agree to use the Platform only for lawful purposes and in accordance with these Terms. You agree not to use the Platform 
              to upload or transmit any content that is unlawful, harmful, or infringes on the rights of others; 
              attempt to gain unauthorized access to other users' accounts or data; 
              interfere with the proper functioning of the Platform; or use automated systems to access the Platform without permission.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">5. Data and Content</h2>
            <p className="text-sm text-gray-700 leading-relaxed">
              You retain ownership of all data and content you upload to the Platform, including candidate information, documents, and notes. 
              By uploading content, you grant Banyak Group a license to store, process, and display that content as necessary to provide the Platform's services. 
              You are responsible for ensuring you have the right to upload and share any content you add to the Platform.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">6. SMS and Text Messaging Program</h2>
            <p className="text-sm text-gray-700 leading-relaxed">
              <strong>SMS messaging is optional.</strong> Consent to receive SMS messages is
              not required to use the Platform, to be considered for any role, or to
              complete any candidate workflow. Candidates who decline SMS will continue to
              receive communications by email and phone as before; their recruitment
              process is not affected.
            </p>
            <p className="text-sm text-gray-700 leading-relaxed">
              The Platform enables authorized recruiters to send SMS text messages only to
              candidates who have explicitly opted in. Consent is collected two ways, both
              optional. First, candidates may opt in themselves when joining our talent
              network at <a href="/talent-network" className="text-blue-600 hover:underline">https://app.banyak.group/talent-network</a>;
              joining requires only a name and email, and the mobile number and SMS consent
              checkbox are an optional, unchecked-by-default add-on, so a candidate can
              complete the signup without providing a number or agreeing to SMS. Second, a
              recruiter already in contact with a candidate may send a personalized consent
              link (<a href="/sms-consent" className="text-blue-600 hover:underline">https://app.banyak.group/sms-consent</a>)
              tied to that candidate's record, where the candidate enters their number and
              ticks an unchecked-by-default consent checkbox; declining is a supported
              one-click path. No SMS is sent to any candidate without a granted consent
              record on file; the platform's outreach executor enforces this server-side.
              For the full opt-in workflow, see <a href="/messaging" className="text-blue-600 hover:underline">https://app.banyak.group/messaging</a>.
            </p>
            <p className="text-sm text-gray-700 leading-relaxed">
              <strong>Message Content:</strong> Messages are limited to recruitment-related communications including interview notifications,
              application status updates, role-specific follow-ups, and recruiter communications tied to the candidate's prior consent. SMS
              is never used for marketing, promotional content, or messages unrelated to the recruitment workflow the candidate consented to.
            </p>
            <p className="text-sm text-gray-700 leading-relaxed">
              <strong>Message Frequency:</strong> Message frequency varies based on recruitment activity and campaign configuration.
              Typical frequency is 1–4 messages per active conversation. Messages are sent only as part of active recruitment workflows and
              are not used for general marketing purposes.
            </p>
            <p className="text-sm text-gray-700 leading-relaxed">
              <strong>Message and Data Rates:</strong> Message and data rates may apply. Please contact your mobile carrier for details about your messaging plan.
            </p>
            <p className="text-sm text-gray-700 leading-relaxed">
              <strong>Opt-Out:</strong> You may opt out of receiving SMS messages at any time by replying STOP to any message received from the Platform.
              After opting out, you will receive a confirmation message and no further SMS messages will be sent. The consent record is marked revoked
              and any future send attempts to the same number are blocked server-side.
            </p>
            <p className="text-sm text-gray-700 leading-relaxed">
              <strong>Help:</strong> For assistance with SMS messages, reply HELP to any message or contact us at <a href="mailto:oban@banyak.group" className="text-blue-600 hover:underline">oban@banyak.group</a>.
            </p>
            <p className="text-sm text-gray-700 leading-relaxed">
              <strong>Supported Carriers:</strong> SMS services are available on major US carriers including AT&T, Verizon, T-Mobile, and others.
              Carrier support is not guaranteed and may be subject to change.
            </p>
            <p className="text-sm text-gray-700 leading-relaxed">
              We do not sell, share, or rent mobile phone numbers or SMS consent information to any third parties.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">7. Voice Calling and Call Recording</h2>
            <p className="text-sm text-gray-700 leading-relaxed">
              The Platform provides an in-app voice calling feature that allows recruiters to place phone calls directly from the Platform. 
              Recruiters may enable call recording for documentation purposes. If call recording is enabled, the call may be recorded and 
              an AI-generated transcript may be created using third-party transcription services. By using the Platform's calling feature, 
              you acknowledge that calls may be recorded when the recording feature is enabled by the recruiter. 
              Call recordings and transcripts are stored securely and accessible only to authorized users within the recruiting organization.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">8. Third-Party Integrations</h2>
            <p className="text-sm text-gray-700 leading-relaxed">
              The Platform offers integrations with third-party services including Google Calendar, Microsoft Outlook, Twilio (for voice and SMS), 
              and OpenAI (for call transcription). 
              Your use of these integrations is subject to the respective terms and privacy policies of those services. 
              You may connect and disconnect these integrations at any time. We are not responsible for the availability or functionality of third-party services.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">9. Confidentiality</h2>
            <p className="text-sm text-gray-700 leading-relaxed">
              Users acknowledge that the Platform contains confidential information about candidates, recruitment processes, and business relationships. 
              You agree to maintain the confidentiality of all information accessed through the Platform and to use it only for its intended recruitment purpose.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">10. Limitation of Liability</h2>
            <p className="text-sm text-gray-700 leading-relaxed">
              To the maximum extent permitted by law, Banyak Group shall not be liable for any indirect, incidental, special, consequential, 
              or punitive damages arising out of or related to your use of the Platform. Our total liability for any claims arising from your use 
              of the Platform shall not exceed the amount you paid to us in the twelve months preceding the claim.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">11. Modifications</h2>
            <p className="text-sm text-gray-700 leading-relaxed">
              We reserve the right to modify these Terms at any time. We will provide notice of material changes via email or through the Platform. 
              Your continued use of the Platform after changes become effective constitutes acceptance of the revised Terms.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">12. Termination</h2>
            <p className="text-sm text-gray-700 leading-relaxed">
              We may suspend or terminate your access to the Platform at our discretion, with or without notice, for conduct that we determine 
              violates these Terms or is harmful to other users or the Platform. Upon termination, your right to use the Platform will immediately cease.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">13. Governing Law</h2>
            <p className="text-sm text-gray-700 leading-relaxed">
              These Terms shall be governed by and construed in accordance with the laws of the United States, without regard to conflict of law principles.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">14. Contact</h2>
            <p className="text-sm text-gray-700 leading-relaxed">
              If you have questions about these Terms, please contact us at <a href="mailto:oban@banyak.group" className="text-blue-600 hover:underline">oban@banyak.group</a>.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
